import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface RouteQueryResult {
  origin: string | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<unknown>;
}

export function useRouteQuery(): RouteQueryResult {
  const sentry = useSentry();
  const [searchParams] = useSearchParams();

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  // Handle validation synchronously
  const validationError = useMemo(() => {
    if (!state) {
      return 'not_found';
    }
    return null;
  }, [state]);

  const {
    data: origin,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['mollie-activation', { code, state, error, errorDescription }],
    queryFn: async () => {
      const response = await qontoClient<{
        data?: { attributes?: { origin?: string } };
      }>('v1/mollie/oauth/redirect', {
        method: 'GET',
        params: {
          state,
          code,
          error,
          error_description: errorDescription,
        },
      });

      const redirectOrigin = response.data?.attributes?.origin;
      if (!redirectOrigin) {
        throw new Error('Missing origin in response');
      }

      return redirectOrigin;
    },
    enabled: !validationError && Boolean(state),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  if (queryError && shouldSendToSentry(queryError)) {
    sentry.captureException(queryError);
  }

  return {
    origin: origin ?? null,
    isLoading: validationError ? false : isLoading,
    error: validationError || queryError?.message || null,
    refetch,
  };
}
